import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { AuthControllerService } from 'src/app/api-client';
import { CompanySetupService } from 'src/app/modules/company-setup/services/company-setup.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { TokenService } from 'src/app/shared/services/token.service';
import {
  CompanyRegistrationResource,
  PublicControllerService,
} from 'src/app/api-client';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private registerRequest: CompanyRegistrationResource = {
    email: null,
    password: null,
    phone1: null,
    companyNo: null,
  };

  public registerSubscription: Subscription;

  public loginSubscription: Subscription;

  public loading: boolean = false;

  constructor(
    private _auth: AuthControllerService,
    private _token: TokenService,
    private _notification: NotificationService,
    private _transloco: TranslocoService,
    private router: Router,
    private _companySetup: CompanySetupService,
    private _public: PublicControllerService
  ) {}

  /** Login to application
   * @param form login form
   */
  public login(form: FormGroup) {
    // Guard against double submit while a login request is in flight
    if (this.loading) return;

    this.loading = true;

    // Get current language
    const currentLang = this._transloco.getActiveLang();

    this.loginSubscription = this._auth
      .login(form.value.identification, form.value.password, null, currentLang)
      .subscribe({
        next: (response) => {
          this.loading = false;

          if (response.user.role.code != 'ROLE_ADMINISTRATOR') {
            this._notification.error(
              this._transloco.translate('login.accessDenied')
            );
            this._token.signOut();
            this.router.navigate(['/auth/login']);
          } else {
            this._token.saveUser(response.user);
            this._token.saveToken(response.token);
            this._token.saveRefreshToken(response.refreshToken);

            response.setupWizard &&
              this._companySetup.setCompanySetupKey(response.setupWizardStep);

            // E-mail not yet verified: the backend still issues a token, so we
            // route the (authenticated) user to the in-app code entry instead
            // of the normal destination. `afterVerify: 'app'` makes the code
            // screen navigate to '' on success, and the AuthGuard +
            // companySetup guard then resume to /setup or the dashboard.
            // Read defensively — undefined (pre client-regen) is NOT a gate.
            if ((response as any)?.emailVerified === false) {
              this.router.navigate(['/verify-email'], {
                state: {
                  email: form.value.identification,
                  afterVerify: 'app',
                },
              });
              return;
            }

            this._notification.success(
              this._transloco.translate('login.loginSuccess')
            );

            // Honor an internal returnUrl (must be an app-internal path)
            const returnUrl =
              this.router.routerState.snapshot.root.queryParamMap.get(
                'returnUrl'
              );
            if (returnUrl && returnUrl.startsWith('/')) {
              this.router.navigateByUrl(returnUrl);
            } else {
              this.router.navigate(['']);
            }
          }
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /** Logout from application */
  public logout() {
    this.loading = true;

    this._auth.logout().subscribe({
      next: () => {
        this.loading = false;
        this._notification.success(
          this._transloco.translate('login.logoutSuccess')
        );
        this._token.signOut();
        this._companySetup.removeCompanySetupKey();
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /** Register user
   * @param company company info form
   * @param auth auth form
   * @param onError optional callback invoked when the registration request fails
   */
  public register(company: FormGroup, auth: FormGroup, onError?: () => void) {
    this.formatForRequest(company);
    this.formatForRequest(auth);

    this.loading = true;

    this.registerSubscription = this._public
      .registerCompany1(this.registerRequest)
      .subscribe({
        next: () => {
          const email = this.registerRequest.email;
          this._notification.success(
            this._transloco.translate('login.verificationCodeSent', { email })
          );
          // Carry the e-mail to the code screen; after verifying, the user is
          // sent to auth/login to sign in (afterVerify: 'login').
          this.router.navigate(['/verify-email'], {
            state: { email, afterVerify: 'login' },
          });
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          onError && onError();
        },
      });
  }

  /** Format form for registration request
   * @param form request form
   */
  private formatForRequest(form: FormGroup) {
    for (let prop in form.controls) {
      this.registerRequest[prop] = form.value[prop];
    }
  }
}
